import type { LineChartOption } from '@devstool/shadcn-echarts'
import type { LineSeriesOption } from 'echarts'
import type { TFunction } from 'i18next'

import { getCSSVariable } from '@devstool/shadcn-echarts'
import { useTranslation } from 'react-i18next'

import { LineChartComponent } from '@/shared/components/line-chart'
import { PALETTE } from '@/shared/lib/theme-palette'

type ElasticityPoint = { bin: number; height: number }

const HIGH_ZONE_COLOR = 'rgba(0, 181, 87, 0.15)'
const LOW_ZONE_COLOR = 'rgba(244, 66, 54, 0.15)'

const getMarkArea = (
	data: ElasticityPoint[],
	t: TFunction
): LineSeriesOption['markArea'] => {
	const bins = data.map(({ bin }) => bin)
	const min = Math.min(...bins)
	const max = Math.max(...bins)
	const step = (max - min) / 3

	const zoneLabel = {
		show: true,
		position: 'insideTopLeft' as const,
		align: 'left' as const,
		color: getCSSVariable('--foreground'),
		fontSize: 12,
	}

	return {
		silent: true,
		data: [
			[
				{
					xAxis: min,
					itemStyle: { color: HIGH_ZONE_COLOR },
					name: t('high'),
					label: zoneLabel,
				},
				{ xAxis: min + step },
			],
			[
				{
					xAxis: min + step,
					itemStyle: { color: 'transparent' },
					name: t('medium'),
					label: zoneLabel,
				},
				{ xAxis: min + step * 2 },
			],
			[
				{
					xAxis: min + step * 2,
					itemStyle: { color: LOW_ZONE_COLOR },
					name: t('low'),
					label: zoneLabel,
				},
				{ xAxis: max },
			],
		],
	}
}

export default function ElasticityDistributionChart({
	data,
}: {
	data: ElasticityPoint[]
}) {
	const { t } = useTranslation()
	const option: LineChartOption = {
		grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },

		xAxis: {
			type: 'value',
			min: 'dataMin',
			max: 'dataMax',
			axisLabel: { show: false },
			axisLine: { show: false },
			axisTick: { show: false },
			splitLine: { show: false },
		},
		yAxis: {
			type: 'value',
			min: 0,
			axisLabel: { show: false },
			axisTick: { show: false },
			splitLine: {
				lineStyle: {
					type: 'dashed',
				},
			},
		},
		series: [
			{
				type: 'line',
				name: t('elasticity'),
				color: PALETTE.blue[75],
				smooth: true,
				symbol: 'none',
				lineStyle: { width: 1 },
				areaStyle: { opacity: 0.8 },
				animation: false,
				data: data.map(({ bin, height }) => [bin, height]),
				markArea: getMarkArea(data, t),
			},
		],
	}

	return <LineChartComponent height={200} option={option} />
}
